/**
 * Recognises `SecurityError`s raised when something on the page reads a
 * restricted property off a CROSS-ORIGIN frame's `Window` — when that something
 * is demonstrably NOT Panda code — so Sentry can drop them before they file as
 * shell crashes.
 *
 *   SecurityError: Failed to read a named property 'history' from 'Window':
 *   Blocked a frame with origin "https://panda.new" from accessing a
 *   cross-origin frame.
 *
 * (Sentry JAVASCRIPT-NEXTJS-F, ~109 events on `/project/:id` since 2026-06-14.)
 *
 * WHY THIS IS NOT OURS. The preview on `/project/:id` is always a cross-origin
 * sandbox iframe (E2B `*.e2b.app`, legacy `*.webcontainer-api.io`, or a Vercel
 * deployment URL), and Panda touches it in exactly two ways, neither of which
 * can raise this:
 *
 *   - `iframe.contentWindow` / `iframe.contentDocument` — these are getters on
 *     the iframe ELEMENT. Cross-origin they hand back an opaque `Window` proxy
 *     and `null` respectively. Neither throws.
 *   - `contentWindow.postMessage(...)` — `postMessage` is on the cross-origin
 *     property allowlist, so reading and calling it is always permitted.
 *
 * Only reading a NON-allowlisted named property (`history`, `location.pathname`,
 * `document`, …) off that opaque proxy produces this message, and the repo does
 * not do that anywhere: there is no `contentWindow.history`, no `window.frames`
 * walk, and no back/forward navigation of the preview. In-iframe navigation is
 * relayed OUT over `postMessage` by the injected bridge (`lib/canvas/inject-bridge.ts`)
 * precisely because direct reads are blocked.
 *
 * The volume agrees: ~2.5 events/day against a route that serves orders of
 * magnitude more pageviews. A Panda code path would fire on essentially every
 * project view. The realistic accessors are browser extensions and the page's
 * third-party scripts (GTM — which also embeds its own cross-origin
 * `googletagmanager.com` iframe on every page — GA, the Meta pixel, PostHog's
 * rrweb recorder), none of which we control.
 *
 * TWO SIGNALS ARE REQUIRED before anything is dropped:
 *
 *   1. The message is the cross-origin frame block — BOTH the "Blocked a frame
 *      with origin" clause AND "from accessing a cross-origin frame". A
 *      different `SecurityError` (blocked storage access, an insecure-context
 *      API) never matches.
 *   2. NO frame in the stack is Panda-owned (`isPandaShellFrameSource`, which
 *      deliberately excludes sandbox-served `/_next/static/` so the generated
 *      app can't masquerade as us). If one of OUR chunks owns the access, the
 *      event survives and gets filed as the real bug it is.
 *
 * Events carrying no usable frames at all count as external and are dropped.
 * That is the deliberate trade-off: an opaque or empty stack is the normal
 * shape for extension- and third-party-raised cross-origin errors, whereas a
 * genuine Panda regression compiles into a `/_next/static/` chunk and will name
 * it. This keeps the drop safe in the direction that matters — we can still see
 * our own bugs.
 *
 * Pure and dependency-free (no Sentry import) so it is trivially unit-testable
 * and reusable on any capture path. It accepts the minimal Sentry event shape it
 * inspects; a real `@sentry/nextjs` `ErrorEvent` satisfies it structurally.
 * Mirrors `dom-mutation-noise.ts` / `in-app-browser-noise.ts` / `error-origin.ts`.
 */

import { isPandaShellFrameSource } from "./error-origin";

export const CROSS_ORIGIN_FRAME_EXTERNAL_ORIGIN = "cross-origin-frame-access" as const;

interface CrossOriginStackFrame {
  filename?: string;
  abs_path?: string;
  module?: string;
}

interface CrossOriginExceptionValue {
  type?: string;
  value?: string;
  stacktrace?: { frames?: CrossOriginStackFrame[] };
}

export interface CrossOriginFrameNoiseEvent {
  /** Sentry's `Event.message` (string in practice; typed loose for safety). */
  message?: unknown;
  exception?: { values?: CrossOriginExceptionValue[] };
}

// The browser's cross-origin frame block. Both halves are required — the
// "Blocked a frame with origin" clause AND the "cross-origin frame" object — so
// an unrelated SecurityError is never silenced by accident. `[\s\S]*` bridges
// the accessing origin, which varies by environment (panda.new, a preview
// deployment, localhost).
const BLOCKED_FRAME_RE =
  /Blocked a frame with origin[\s\S]*from accessing a cross-origin frame/i;

function matchesBlockedFrame(text: string): boolean {
  return BLOCKED_FRAME_RE.test(text);
}

function frameIsPandaOwned(frame: CrossOriginStackFrame): boolean {
  return isPandaShellFrameSource(frame.filename ?? frame.abs_path ?? frame.module);
}

/**
 * True when the event is a cross-origin frame-access `SecurityError` that no
 * Panda-owned stack frame is responsible for, and so should be dropped from
 * error reporting.
 *
 * Returns FALSE — keep the event — when a Panda bundle frame owns the access,
 * because that would be a real bug in our iframe handling.
 */
export function isExternalCrossOriginFrameNoise(
  event: CrossOriginFrameNoiseEvent | null | undefined,
): boolean {
  if (!event) return false;

  let matched = false;

  for (const value of event.exception?.values ?? []) {
    const combined = `${value.type ?? ""}: ${value.value ?? ""}`;
    if (matchesBlockedFrame(combined)) matched = true;

    // Any Panda-owned frame anywhere in the event means we own the access —
    // keep it, whatever the message said.
    for (const frame of value.stacktrace?.frames ?? []) {
      if (frameIsPandaOwned(frame)) return false;
    }
  }

  // Fallback for events carrying only a top-level message (no exception values).
  if (!matched) {
    const message = typeof event.message === "string" ? event.message : "";
    matched = matchesBlockedFrame(message);
  }

  return matched;
}
